import React, { useState, useEffect, useCallback } from 'react';
import { getTransactions, getTransactionStatistics, updateTransaction, bulkUpdateTransactions, recordPayout, syncOrdersToTransactions } from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useToast } from '../../contexts/ToastContext';

function Transactions() {
  const { showSuccess, showError } = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalSales: 0,
    salesPending: 0,
    salesReceived: 0,
    totalPayouts: 0,
    dueToBusinessOwner: 0,
    todaySales: 0,
    weekSales: 0,
    monthSales: 0,
    commission: 0,
    smsCost: 0,
  });
  
  const [filters, setFilters] = useState({
    type: 'all', // all, sale, payout
    status: 'all', // all, pending, received, paid
    date_filter: 'all', // all, today, week, month, year, custom
    start_date: '',
    end_date: '',
  });
  
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    payout_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [syncingTransactions, setSyncingTransactions] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      
      const data = await getTransactions(token, filters);
      setTransactions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      showError(error.message || 'Failed to load transactions. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const stats = await getTransactionStatistics(token, filters.date_filter);
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, [filters.date_filter]);

  useEffect(() => {
    fetchTransactions();
    fetchStatistics();
  }, [fetchTransactions, fetchStatistics]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleStatusUpdate = async (transactionId, newStatus, hubtelDate = null, payoutDate = null) => {
    setUpdatingStatus(transactionId);
    try {
      const token = localStorage.getItem('auth_token');
      const updateData = { status: newStatus };
      if (hubtelDate) updateData.hubtel_transfer_date = hubtelDate;
      if (payoutDate) updateData.payout_date = payoutDate;
      
      await updateTransaction(token, transactionId, updateData);
      showSuccess(`Transaction status updated to ${newStatus}`);
      await fetchTransactions();
      await fetchStatistics();
    } catch (error) {
      console.error('Failed to update transaction status:', error);
      showError(error.message || 'Failed to update transaction status.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle bulk mark as received
  const handleBulkMarkReceived = async () => {
    if (selectedTransactions.size === 0) {
      showError('Please select at least one transaction to mark as received.');
      return;
    }

    // Filter to only pending sale transactions
    const pendingSaleIds = transactions
      .filter(t => 
        selectedTransactions.has(t.id) && 
        t.type === 'sale' && 
        t.status === 'pending'
      )
      .map(t => t.id);

    if (pendingSaleIds.length === 0) {
      showError('Please select pending sale transactions to mark as received.');
      return;
    }

    setBulkUpdating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const hubtelDate = new Date().toISOString().split('T')[0];
      
      await bulkUpdateTransactions(token, pendingSaleIds, 'received', hubtelDate);
      showSuccess(`Successfully marked ${pendingSaleIds.length} transaction(s) as received`);
      
      // Clear selection
      setSelectedTransactions(new Set());
      
      // Refresh data
      await fetchTransactions();
      await fetchStatistics();
    } catch (error) {
      console.error('Failed to bulk update transactions:', error);
      showError(error.message || 'Failed to mark transactions as received.');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Toggle transaction selection
  const handleToggleSelection = (transactionId) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  // Toggle select all
  const handleSelectAll = () => {
    // Only select pending sale transactions
    const pendingSales = transactions.filter(t => t.type === 'sale' && t.status === 'pending');
    const pendingSaleIds = new Set(pendingSales.map(t => t.id));
    
    if (selectedTransactions.size === pendingSaleIds.size && 
        Array.from(selectedTransactions).every(id => pendingSaleIds.has(id))) {
      // All selected, deselect all
      setSelectedTransactions(new Set());
    } else {
      // Select all pending sales
      setSelectedTransactions(pendingSaleIds);
    }
  };

  // Get pending sale transactions count
  const pendingSaleTransactions = transactions.filter(t => t.type === 'sale' && t.status === 'pending');
  const selectedPendingSales = transactions.filter(t => 
    selectedTransactions.has(t.id) && t.type === 'sale' && t.status === 'pending'
  );

  const handlePayoutSubmit = async (e) => {
    e.preventDefault();
    setSubmittingPayout(true);
    try {
      const token = localStorage.getItem('auth_token');
      await recordPayout(
        token,
        parseFloat(payoutForm.amount),
        payoutForm.payout_date || null,
        payoutForm.notes || null
      );
      showSuccess('Payout recorded successfully');
      setShowPayoutModal(false);
      setPayoutForm({
        amount: '',
        payout_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      await fetchTransactions();
      await fetchStatistics();
    } catch (error) {
      console.error('Failed to record payout:', error);
      showError(error.message || 'Failed to record payout.');
    } finally {
      setSubmittingPayout(false);
    }
  };

  const handleSyncTransactions = async () => {
    setSyncingTransactions(true);
    try {
      const token = localStorage.getItem('auth_token');
      const result = await syncOrdersToTransactions(token);
      showSuccess(`Sync completed: ${result.transactions_created} transactions created`);
      await fetchTransactions();
      await fetchStatistics();
    } catch (error) {
      console.error('Failed to sync transactions:', error);
      showError(error.message || 'Failed to sync transactions.');
    } finally {
      setSyncingTransactions(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      received: 'bg-blue-100 text-blue-800 border-blue-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getTypeColor = (type) => {
    return type === 'sale'
      ? 'bg-green-100 text-green-800 border-green-300'
      : 'bg-red-100 text-red-800 border-red-300';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Transactions</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage sales and payouts</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {selectedPendingSales.length > 0 && (
            <button
              onClick={handleBulkMarkReceived}
              disabled={bulkUpdating}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
            >
              {bulkUpdating ? 'Updating...' : `Mark ${selectedPendingSales.length} as Received`}
            </button>
          )}
          <button
            onClick={handleSyncTransactions}
            disabled={syncingTransactions}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
          >
            {syncingTransactions ? 'Syncing...' : 'Sync Transactions'}
          </button>
          <button
            onClick={() => setShowPayoutModal(true)}
            className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors shadow-lg hover:shadow-xl"
          >
            Record Payout
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Sales */}
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Total Sales</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(statistics.totalSales)}</div>
          <div className="text-xs text-gray-500 mt-2">
            Today: {formatCurrency(statistics.todaySales)} | Week: {formatCurrency(statistics.weekSales)} | Month: {formatCurrency(statistics.monthSales)}
          </div>
        </div>
        
        {/* Due to Business Owner */}
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div className="text-sm text-gray-600">Due to Business Owner</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(statistics.dueToBusinessOwner)}</div>
          <div className="text-xs text-gray-500 mt-2">Received - Payouts: {formatCurrency(statistics.totalPayouts)}</div>
        </div>
        
        {/* Received from Hubtel */}
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">Received from Hubtel</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(statistics.salesReceived)}</div>
          <div className="text-xs text-gray-500 mt-2">Ready for payout</div>
        </div>
        
        {/* Commission */}
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-orange-500">
          <div className="text-sm text-gray-600">Commission</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(statistics.commission || 0)}</div>
          <div className="text-xs text-gray-500 mt-2">Platform commission fees</div>
        </div>
        
        {/* SMS Cost */}
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-pink-500">
          <div className="text-sm text-gray-600">SMS Cost</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(statistics.smsCost || 0)}</div>
          <div className="text-xs text-gray-500 mt-2">SMS notification costs</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Types</option>
              <option value="sale">Sales</option>
              <option value="payout">Payouts</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="received">Received</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={filters.date_filter}
              onChange={(e) => handleFilterChange('date_filter', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {filters.date_filter === 'custom' && (
            <div className="col-span-2 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No transactions found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    {pendingSaleTransactions.length > 0 && (
                      <input
                        type="checkbox"
                        checked={selectedPendingSales.length === pendingSaleTransactions.length && pendingSaleTransactions.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                        title="Select all pending sales"
                      />
                    )}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hubtel Transfer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payout Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => {
                  const isPendingSale = transaction.type === 'sale' && transaction.status === 'pending';
                  const isSelected = selectedTransactions.has(transaction.id);
                  
                  return (
                    <tr 
                      key={transaction.id} 
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isPendingSale ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelection(transaction.id)}
                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                          />
                        ) : (
                          <span className="w-4 h-4 inline-block"></span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(transaction.created_at)}
                      </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getTypeColor(transaction.type)}`}>
                        {transaction.type === 'sale' ? 'Sale' : 'Payout'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {transaction.order?.order_number || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {transaction.type === 'sale' ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(transaction.hubtel_transfer_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(transaction.payout_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {transaction.type === 'sale' && transaction.status === 'pending' && (
                        <button
                          onClick={() => handleStatusUpdate(transaction.id, 'received', new Date().toISOString().split('T')[0])}
                          disabled={updatingStatus === transaction.id}
                          className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                        >
                          {updatingStatus === transaction.id ? 'Updating...' : 'Mark Received'}
                        </button>
                      )}
                      {transaction.type === 'payout' && transaction.status === 'pending' && (
                        <button
                          onClick={() => handleStatusUpdate(transaction.id, 'paid', null, new Date().toISOString().split('T')[0])}
                          disabled={updatingStatus === transaction.id}
                          className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                        >
                          {updatingStatus === transaction.id ? 'Updating...' : 'Mark Paid'}
                        </button>
                      )}
                      {transaction.notes && (
                        <div className="mt-1 text-xs text-gray-500" title={transaction.notes}>
                          {transaction.notes.substring(0, 30)}...
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Record Payout</h2>
            <form onSubmit={handlePayoutSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={payoutForm.amount}
                    onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payout Date</label>
                  <input
                    type="date"
                    required
                    value={payoutForm.payout_date}
                    onChange={(e) => setPayoutForm({ ...payoutForm, payout_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    value={payoutForm.notes}
                    onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                    rows="3"
                    placeholder="Additional notes about this payout"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowPayoutModal(false);
                    setPayoutForm({
                      amount: '',
                      payout_date: new Date().toISOString().split('T')[0],
                      notes: '',
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingPayout}
                  className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-md font-semibold disabled:opacity-50"
                >
                  {submittingPayout ? 'Recording...' : 'Record Payout'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Transactions;
